import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Fuel, Zap, Sparkles, Trophy, MapPin, Calendar } from "lucide-react";
import { api } from "@/lib/api";
import { FuelGauge, TierPill, FuelInstructionsModal, ComingSoonPanel } from "@/components/CommunityTankSection";

/**
 * /community-tank — full Community LootFund dashboard.
 *
 * Layout (top → bottom):
 *   1. Hero with fuel gauge + live "current tank level" + Fuel CTA
 *   2. Pledge card ("100% of community donations go back to prizes")
 *   3. Wall of Igniters (pinned + sorted by lifetime contribution)
 *   4. Wall of Boosters / Sparks
 *   5. Loot History (transparency log of every tank-funded drop)
 *   6. Disclaimer fine print
 *
 * Toggle off → ComingSoonPanel with email capture.
 */
export default function CommunityTankPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/tank/status")
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, []);

  if (error) {
    return (
      <div className="splash-root flex items-center justify-center" data-testid="tank-error">
        <div className="splash-mono opacity-60">Couldn't load the tank.</div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="splash-root flex items-center justify-center" data-testid="tank-loading">
        <div className="splash-mono opacity-60">Priming the pump…</div>
      </div>
    );
  }
  if (!data.enabled) {
    return (
      <div className="splash-root" data-testid="community-tank-page">
        <ComingSoonPanel data={data} />
      </div>
    );
  }

  const s = data.settings || {};
  const t = data.tank || {};
  const honor = data.honor_roll || {};
  const history = data.loot_history || [];

  return (
    <div className="splash-root" data-testid="community-tank-page">
      <Hero s={s} t={t} latest={data.latest_fuel} onFuel={() => setOpen(true)} />
      <PledgeBand s={s} />
      <Wall title="The Igniters" subtitle="Lit the fuse with $100 or more." rows={honor.igniters || []} tier="igniter" testid="wall-igniters" />
      <Wall title="The Boosters" subtitle="$20+ pushed the tank closer." rows={honor.boosters || []} tier="booster" testid="wall-boosters" />
      <Wall title="The Sparks" subtitle="Started something. Every dollar counts." rows={honor.sparks || []} tier="spark" testid="wall-sparks" />
      <LootHistory history={history} />
      <DisclaimerBand s={s} />
      <FuelInstructionsModal open={open} onClose={() => setOpen(false)} settings={s} />
    </div>
  );
}

function Hero({ s, t, latest, onFuel }) {
  return (
    <section className="splash-hero relative" data-testid="tank-hero">
      <div className="splash-hero-overlay" aria-hidden="true" />
      <div className="splash-grain" aria-hidden="true" />
      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 pt-10 md:pt-16 pb-12 md:pb-16">
        <Link
          to="/"
          className="splash-mono uppercase tracking-[0.22em] text-[11px] text-[#76B82A] inline-flex items-center gap-2 hover:text-[#84d130]"
          data-testid="tank-back"
        >
          <ArrowLeft size={12} /> Back to The Local Loot
        </Link>
        <div className="mt-8 grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="splash-mono uppercase tracking-[0.22em] text-[11px] text-[#d7a62a]">
              {s.page_overline || "100% Community-Powered"}
            </div>
            <h1 className="splash-display text-5xl sm:text-6xl md:text-7xl uppercase mt-3 leading-[0.95] text-[#fffaef]">
              <span className="inline-flex items-center gap-3 flex-wrap">
                <Fuel size={48} className="text-[#76B82A]" />
                Community LootFund
              </span>
            </h1>
            <div className="mt-3 splash-display text-2xl md:text-3xl uppercase text-[#76B82A]">
              {s.page_headline || "The Community Tank."}
            </div>
            <p className="mt-5 max-w-2xl text-[15px] md:text-base text-[#f7f3e6]/80 leading-relaxed">
              {s.page_subhead}
            </p>
            {latest && (
              <div className="mt-5 inline-flex items-center gap-2 splash-mono uppercase tracking-widest text-[11px] text-[#76B82A]">
                <Zap size={12} fill="#76B82A" /> Latest fuel: {latest.display_name} · {latest.amount_label}
              </div>
            )}
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={onFuel}
                className="btn-loot-solid"
                data-testid="tank-hero-fuel-cta"
              >
                Fuel the Tank
              </button>
            </div>
          </div>
          <div className="lg:col-span-5">
            <article className="bg-[#fffaef] border-2 border-[#0d100d] shadow-[10px_10px_0_rgba(118,184,42,0.55)] p-6 md:p-8 text-center" data-testid="tank-hero-gauge">
              <div className="splash-mono uppercase tracking-[0.22em] text-[10px] text-[#76B82A]">
                Available in tank
              </div>
              <div className="splash-display text-6xl md:text-7xl text-[#0d100d] mt-1 leading-none">
                {t.available_label}
              </div>
              <div className="splash-mono text-[11px] text-[#0d100d]/65 mt-2">
                Target: {t.target_label} · Raised lifetime: {t.raised_label}
              </div>
              <div className="mt-5">
                <FuelGauge progress={t.progress_pct} size={260} label="Fueling the next adventure" />
              </div>
              <div className="mt-3 splash-mono text-[10px] uppercase tracking-widest text-[#0d100d]/65">
                {t.donor_count} hunter{t.donor_count === 1 ? "" : "s"} fueled · {t.spent_label} already deployed
              </div>
            </article>
          </div>
        </div>
      </div>
    </section>
  );
}

function PledgeBand({ s }) {
  return (
    <section className="cream-section py-10 md:py-14" data-testid="tank-pledge">
      <div className="relative z-10 max-w-3xl mx-auto px-4 md:px-8 text-center">
        <div className="splash-mono uppercase tracking-[0.22em] text-[11px] text-[#76B82A]">
          The pledge
        </div>
        <p className="splash-display text-2xl md:text-4xl uppercase mt-3 text-[#0d100d] leading-tight">
          {s.page_pledge || "100% of community donations go back to the prize pool. No overhead. No cuts. Just loot."}
        </p>
      </div>
    </section>
  );
}

function Wall({ title, subtitle, rows, tier, testid }) {
  if (!rows || rows.length === 0) return null;
  const onCream = tier === "booster";  // alternate light/dark for visual rhythm
  const sectionCls = onCream
    ? "cream-section py-12 md:py-16"
    : "bg-[#0d100d] border-y border-[#76B82A]/15 py-12 md:py-16";
  const headlineCls = onCream ? "text-[#0d100d]" : "text-[#fffaef]";
  const subCls = onCream ? "text-[#0d100d]/65" : "text-[#f7f3e6]/65";
  return (
    <section className={sectionCls} data-testid={testid}>
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="splash-mono uppercase tracking-[0.22em] text-[11px] text-[#d7a62a]">
          Wall of {title.split(" ").pop()}
        </div>
        <h2 className={`splash-display text-3xl md:text-5xl uppercase mt-2 ${headlineCls}`}>
          {title}
        </h2>
        <p className={`mt-2 max-w-2xl text-[14px] ${subCls}`}>{subtitle}</p>
        <div className={`mt-7 grid ${tier === "spark" ? "sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4" : "sm:grid-cols-2 lg:grid-cols-3"} gap-4`}>
          {rows.map((r, i) => (
            <DonorCard key={`${tier}-${i}`} row={r} tier={tier} />
          ))}
        </div>
      </div>
    </section>
  );
}

function DonorCard({ row, tier }) {
  const accent = tier === "igniter" ? "#76B82A" : tier === "booster" ? "#d7a62a" : "#f7f3e6";
  const onCard = tier === "booster"
    ? "bg-[#0d100d] text-[#fffaef]"
    : "bg-[#fffaef] text-[#0d100d]";
  return (
    <article
      className={`${onCard} border-2 border-[#0d100d] p-5 ${row.pinned ? "shadow-[8px_8px_0_rgba(118,184,42,0.7)]" : "shadow-[4px_4px_0_#0d100d]"} ${tier === "igniter" ? "relative" : ""}`}
      data-testid={`donor-card-${row.username || row.display_name}`}
    >
      {tier === "igniter" && (
        <div className="absolute -top-3 -right-3 w-9 h-9 rounded-full bg-[#0d100d] border-2 border-[#76B82A] flex items-center justify-center shadow-[0_0_14px_rgba(118,184,42,0.7)] animate-pulse-slow">
          <Zap size={14} fill="#76B82A" stroke="#76B82A" />
        </div>
      )}
      <div className="flex items-center gap-3">
        <div
          className="w-12 h-12 rounded-full bg-cover bg-center border-2 border-[#0d100d] flex items-center justify-center shrink-0"
          style={{
            backgroundImage: row.avatar_url ? `url(${row.avatar_url})` : undefined,
            backgroundColor: accent,
          }}
        >
          {!row.avatar_url && (
            <span className="splash-display text-xl">
              {(row.display_name || "?")[1] || (row.display_name || "?")[0]}
            </span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          {row.username ? (
            <Link
              to={`/u/${encodeURIComponent(row.username)}`}
              className="splash-display text-xl uppercase truncate hover:text-[#76B82A] transition-colors block"
            >
              {row.display_name}
            </Link>
          ) : (
            <div className="splash-display text-xl uppercase truncate">{row.display_name}</div>
          )}
          {row.city && <div className="splash-mono text-[10px] uppercase tracking-widest opacity-55 truncate">{row.city}</div>}
        </div>
        <TierPill tier={tier} />
      </div>
      <div className="mt-3 flex items-baseline justify-between gap-3">
        <span className="splash-display text-3xl">{row.amount_label}</span>
        <span className="splash-mono uppercase tracking-widest text-[10px] opacity-55">contributed</span>
      </div>
      {row.note && tier === "igniter" && (
        <p className="mt-3 text-[13px] leading-relaxed italic">"{row.note}"</p>
      )}
    </article>
  );
}

function LootHistory({ history }) {
  return (
    <section className="cream-section py-12 md:py-16" data-testid="tank-loot-history">
      <div className="relative z-10 max-w-5xl mx-auto px-4 md:px-8">
        <div className="splash-mono uppercase tracking-[0.22em] text-[11px] text-[#d7a62a]">
          Receipts
        </div>
        <h2 className="splash-display text-3xl md:text-5xl uppercase mt-2 text-[#0d100d]">
          Loot History
        </h2>
        <p className="mt-2 max-w-xl text-[14px] text-[#0d100d]/65">
          Every People's Drop the tank has funded — exactly which prize, which town, which local shop we bought from.
        </p>
        {history.length === 0 ? (
          <div className="mt-8 bg-[#fffaef] border-2 border-[#0d100d] p-6 text-center">
            <div className="splash-display text-2xl uppercase text-[#0d100d]">No drops yet.</div>
            <p className="mt-2 text-[14px] text-[#0d100d]/65">The first People's Drop is loading. Fuel the tank to get us there.</p>
          </div>
        ) : (
          <ol className="mt-7 space-y-3">
            {history.map((h) => (
              <li
                key={h.id}
                className="bg-[#fffaef] border-2 border-[#0d100d] p-4 md:p-5 grid md:grid-cols-12 gap-3 md:gap-5 items-start"
                data-testid={`loot-history-${h.id}`}
              >
                <div className="md:col-span-7">
                  <div className="splash-display text-xl md:text-2xl uppercase text-[#0d100d] leading-tight">{h.title}</div>
                  {h.note && <p className="mt-1 text-[13px] text-[#0d100d]/70">{h.note}</p>}
                  <div className="mt-2 flex items-center gap-3 flex-wrap">
                    {h.city && (
                      <span className="splash-mono uppercase tracking-widest text-[10px] text-[#76B82A] inline-flex items-center gap-1">
                        <MapPin size={10} /> {h.city}
                      </span>
                    )}
                    {h.happened_at && (
                      <span className="splash-mono uppercase tracking-widest text-[10px] text-[#0d100d]/55 inline-flex items-center gap-1">
                        <Calendar size={10} /> {new Date(h.happened_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
                <div className="md:col-span-5 flex flex-col md:items-end md:text-right gap-1">
                  <div
                    className="splash-display text-2xl md:text-3xl leading-none flex items-center gap-1"
                    style={{ color: h.is_mystery ? "#D7A62A" : "#76B82A" }}
                    data-testid={h.is_mystery ? `tank-receipt-mystery-${h.id || h.title}` : undefined}
                  >
                    {h.is_mystery && <span>🎁</span>}
                    {h.amount_label}
                  </div>
                  {h.sponsor_business && (
                    <div className="splash-mono uppercase tracking-widest text-[10px] text-[#0d100d]/65">
                      <Trophy size={10} className="inline mr-1" />
                      Bought from {h.sponsor_business}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>
    </section>
  );
}

function DisclaimerBand({ s }) {
  if (!s.disclaimer) return null;
  return (
    <section className="bg-[#0d100d] py-8" data-testid="tank-disclaimer">
      <div className="max-w-3xl mx-auto px-4 md:px-8">
        <p className="splash-mono uppercase text-[10px] tracking-widest text-[#f7f3e6]/35 leading-relaxed text-center">
          {s.disclaimer}
        </p>
      </div>
    </section>
  );
}
